/**
 * DueDateBadge Component (Phase 7 Batch 2)
 *
 * Displays a due date with color-coded urgency based on days until due.
 * Reusable for billable items, permits, and any future due date tracking.
 *
 * Color coding:
 * - Red: Overdue
 * - Dark Orange: Critical (< 7 days)
 * - Light Orange: Warning (7-30 days)
 * - Yellow: Upcoming (30-60 days)
 * - Gray: On-track (> 60 days)
 */

import React from 'react';
import { getDueDateUrgency, formatDueDate, type DueDateThresholds } from '../utils/dueDateUrgency';

interface DueDateBadgeProps {
  dueDate: string | Date | null | undefined;
  /** Show full date (e.g., "Dec 1, 2024") in addition to urgency label */
  showDate?: boolean;
  /** Custom urgency thresholds (optional) */
  thresholds?: Partial<DueDateThresholds>;
  /** Additional CSS classes */
  className?: string;
}

export function DueDateBadge({
  dueDate,
  showDate = false,
  thresholds,
  className = '',
}: DueDateBadgeProps) {
  const urgency = getDueDateUrgency(dueDate, thresholds);

  if (!urgency) {
    return null;
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${urgency.colorClass} ${urgency.textColorClass} ${className}`}
      title={formatDueDate(dueDate)}
    >
      {urgency.label}
      {showDate && (
        <span className="text-xs opacity-75">
          ({formatDueDate(dueDate)})
        </span>
      )}
    </span>
  );
}
